import { useContext, useEffect, useRef, useState } from "react";
import { Loading, useRedirect } from "react-admin";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import { exhibitorEdit as classes } from "../ExhibitorEdit/styles";
import { SettingBox, LeadRetrievalSettings as settingStyle } from "./styles";

import { APIContext } from "../../store/APIStore";
import { StoreContext } from "../../store/Store";

import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";

import FdTypography from "../../components/UIComponents/Typography/FdTypography";
import FdButton from "../../components/UIComponents/Button/FdButton";
import FdIconButton from "../../components/UIComponents/IconButton/FdIconButton";
import FdTextField from "../../components/UIComponents/TextField/FdTextfield";
import FdSwitch from "../../components/UIComponents/Switch/FdSwitch";
import RouteLeavingGuard from "../../components/RouteLeavingGuard/RouteLeavingGuard";
import { Wrapper } from "../../CommonStyles";
import { IGdprConfig } from "../../dataProvider/interfaces/ILeads";
import { routePaths } from "../../baseLayout/SidebarMenu";
import LeadFieldMappingForm from "./LeadFieldMappingForm";
import SimpleActionDialog from "../../components/SimpleActionDialog/SimpleActionDialog";
import Grid from "@mui/material/Grid";

const LeadSettings = () => {
  const { eventId } = useParams() as any;
  const [state, dispatch] = useContext(StoreContext);
  const api = useContext(APIContext);
  const notificationRef = useRef<any>();
  const navigate = useNavigate();
  const redirect = useRedirect();
  const [loading, setLoading] = useState(true); // data loading flag
  const [settings, setSettings] = useState<IGdprConfig>();

  const [actionDialog, setActionDialog] = useState({
    open: false,
    title: "",
    loading: false,
    content: <></>,
    fullWidth: false,
    maxWidth: "",
  });

  const initialValues = {
    consent: settings?.gdprConfig?.gdprConsent,
    approveButtonLabel: settings?.gdprConfig?.approveButtonLabel,
    cancelButtonLabel: settings?.gdprConfig?.cancelButtonLabel,
    title: settings?.gdprConfig?.title,
    name: settings?.name,
    description: settings?.gdprConfig?.description,
    enableReports: settings?.dailyReportsEnabled,
    apiKey: settings?.apiKey,
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty, errors },
  } = useForm({
    defaultValues: initialValues,
    mode: "onBlur",
  });

  useEffect(() => {
    reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, settings]);

  const consentValue = watch("consent", settings?.gdprConfig?.gdprConsent);

  useEffect(() => {
    if (!consentValue) {
      setValue("title", "");
      setValue("description", "");
      setValue("approveButtonLabel", "");
      setValue("cancelButtonLabel", "");
    }
  }, [consentValue, setValue]);

  const updateSettings = async (values: any) => {
    try {
      setLoading(true);
      const settings: any = await api.leadsService.updateSettings(
        eventId,
        values
      );
      setSettings(settings.data);
      await fetchSettings();
      setLoading(false);
      notificationRef.current?.show(
        settings?.message,
        notificationType.Success
      );
    } catch (error: any) {
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const onSubmit = (values: any) => {
    const payload = {
      dailyReportsEnabled: values.enableReports,
      archivalDays: state?.selectedEvent?.archivingPeriod,
      name: values.name,
      apiKey: values.apiKey,
      gdprConfig: {
        approveButtonLabel: values.approveButtonLabel,
        cancelButtonLabel: values.cancelButtonLabel,
        title: values.title,
        description: values.description,
        gdprConsent: values.consent,
      },
    };
    updateSettings(payload);
  };

  useEffect(() => {
    // fetch the selected event if it wasn't set in the store
    if (eventId && !state.selectedEvent) {
      fetchSelectedEvent();
    }
    if (eventId) {
      fetchSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const getSettings = (await api.leadsService.getSettingDetail(
        eventId
      )) as any;
      setSettings(getSettings.data);
      setLoading(false);
    } catch (error: any) {
      console.log(error);
      notificationRef.current?.show(
        error.response?.data.message,
        notificationType.Error
      );
    }
  };

  const fetchSelectedEvent = async () => {
    try {
      const event = await api.eventService.getEvent({ id: eventId });
      dispatch({ type: "SET_SELECTED_EVENT", payload: event });
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data?.message,
        notificationType.Error
      );
    }
  };

  // the step where we show the csv map form
  const showMappingForm = () => {
    setActionDialog({
      open: true,
      loading: false,
      title: "",
      fullWidth: true,
      maxWidth: "xl",
      content: <LeadFieldMappingForm onCancel={closeDialog} />,
    });
  };
  // close the dialog
  const closeDialog = () => {
    setActionDialog({
      open: false,
      content: <></>,
      title: "",
      loading: false,
      fullWidth: false,
      maxWidth: "",
    });
  };

  return (
    <>
      <Box sx={classes.wrapper}>
        <Wrapper sx={{ width: "100%" }}>
          <Box sx={classes.topActions} aria-label="topActions">
            <Box sx={classes.infoTitle}>
              <Box textAlign="left" sx={{ flexGrow: 1 }}>
                <Box sx={settingStyle.title}>
                  <FdTypography label="Settings" variant="h2" component="h2" />
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FdButton
                variant="contained"
                color="primary"
                size="small"
                type="submit"
                sx={{ mr: 2 }}
                form="eventForm"
                disabled={!isDirty}
              >
                Save
              </FdButton>

              <FdIconButton
                aria-label="close"
                onClick={() => {
                  redirect(
                    `/${routePaths.EVENTS}/${state.selectedEvent?.id}/${routePaths.EXHIBITOR}`
                  );
                }}
                size="large"
                Icon={<CloseIcon />}
              />
            </Box>
          </Box>
          {loading ? (
            <Loading />
          ) : (
            <form id="eventForm" onSubmit={handleSubmit(onSubmit)} noValidate>
              <SettingBox>
                {/* map fields */}
                <Box sx={settingStyle.flexXYCenter}>
                  <FdTypography
                    label="Map app fields with the portal fields"
                    sx={settingStyle.settingTitle}
                  />
                  <FdButton
                    size="small"
                    ButtonName=" Map fields"
                    onClick={() => showMappingForm()}
                    sx={{ fontSize: "14px" }}
                  />
                </Box>

                {/* email daily */}
                <Box>
                  <FdSwitch
                    register={register}
                    name="enableReports"
                    label="Email daily reports"
                    placement="start"
                    defaultChecked={initialValues.enableReports}
                    sx={settingStyle.enableReportStyle}
                  />
                </Box>

                {/* GDPR config */}
                <Box>
                  <Box sx={settingStyle.flexXYCenter}>
                    <FdTypography
                      label="GDPR config"
                      sx={settingStyle.settingTitle}
                    />
                    <FdSwitch
                      register={register}
                      name="consent"
                      placement="start"
                      defaultChecked={consentValue}
                    />
                  </Box>
                  {consentValue && (
                    <Grid container spacing={2} sx={classes.formFields}>
                      <Grid item xs={12} md={6}>
                        <FdTextField
                          register={register}
                          label="Enter title"
                          name="title"
                          placeholder="enter title of your GDPR consent form"
                          disabled={!consentValue}
                          required={consentValue}
                          error={Boolean(errors.title) && consentValue}
                          errorMsg={
                            errors.title && consentValue && "Title is required"
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FdTextField
                          register={register}
                          label="Enter description"
                          name="description"
                          placeholder="Enter description here"
                          disabled={!consentValue}
                          required={consentValue}
                          error={Boolean(errors.description) && consentValue}
                          errorMsg={
                            errors.description &&
                            consentValue &&
                            "Description is required"
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FdTextField
                          register={register}
                          label="Enter approve button label"
                          name="approveButtonLabel"
                          placeholder="e.g. Save, okay"
                          disabled={!consentValue}
                          required={consentValue}
                          error={
                            Boolean(errors.approveButtonLabel) && consentValue
                          }
                          errorMsg={
                            errors.approveButtonLabel &&
                            consentValue &&
                            "Approve button is required"
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FdTextField
                          register={register}
                          label="Enter cancel button label"
                          name="cancelButtonLabel"
                          placeholder="e.g. cancel, exit"
                          disabled={!consentValue}
                          required={consentValue}
                          error={
                            Boolean(errors.cancelButtonLabel) && consentValue
                          }
                          errorMsg={
                            errors.cancelButtonLabel &&
                            consentValue &&
                            "Cancel button is required"
                          }
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>

                {/* other settings */}
                <Box>
                  <FdTypography
                    label="Other settings"
                    sx={settingStyle.settingTitle}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FdTextField
                        register={register}
                        label="Event name (in reports)"
                        name="name"
                        placeholder="enter name of the event to be shown in reports and emails"
                        required={true}
                        error={Boolean(errors.name)}
                        errorMsg={errors.name && "Event name is required"}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FdTextField
                        register={register}
                        label="API key"
                        name="apiKey"
                        disabled
                        placeholder="enter API key"
                        // required={true}
                        sx={{   "& .Mui-disabled": {
                          cursor: "default",
                          "&.MuiInputBase-root":{
                            backgroundColor:"#ededed",
                            boxShadow:"none",
                          }
                        },}}
                        // error={Boolean(errors.apiKey)}
                        // errorMsg={errors.apiKey && "API key is required"}
                        // helperText="Please add the developer api key of the admin user for the event account only."
                      />
                    </Grid>
                  </Grid>
                </Box>
              </SettingBox>
            </form>
          )}
          <NotificationMessage ref={notificationRef} />
          <RouteLeavingGuard
            when={isDirty}
            navigate={(path) => navigate(path)}
            shouldBlockNavigation={() => isDirty}
          />
        </Wrapper>

        {actionDialog.open && (
          <SimpleActionDialog
            open={actionDialog.open}
            title={actionDialog.title}
            content={!actionDialog.loading ? actionDialog.content : ""}
            maxWidth="xl"
            fullWidth={actionDialog.fullWidth} // for certain steps we need full width dialog
            isLoading={actionDialog.loading}
            onClose={() => {
              closeDialog();
            }}
          />
        )}
      </Box>
    </>
  );
};

export default LeadSettings;
